import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { fetchRecipes } from "../lib/api";
import { Clock, Baby, Utensils, Sparkles, ArrowLeft, ArrowRight } from "lucide-react";
import SaveRecipeButton from "./SaveRecipeButton";

// Slight per-card rotation for a hand-pinned "recipe card wall" feel.
const CARD_TILTS = ["-rotate-1", "rotate-1", "-rotate-[0.5deg]", "rotate-[0.5deg]", "-rotate-1", "rotate-1"];

export default function Recipes() {
  const [recipes, setRecipes] = useState([]);
  const scrollerRef = useRef(null);

  useEffect(() => {
    fetchRecipes().then(setRecipes).catch((err) => console.warn("[fetch] failed:", err));
  }, []);

  const scroll = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 360, behavior: "smooth" });
  };

  return (
    <section id="recipes" data-testid="section-recipes" className="relative py-20 md:py-28 bg-pb-cream overflow-hidden scroll-mt-24">
      <div className="blob bg-pb-sky" style={{ width: 360, height: 360, top: -100, left: -80, opacity: 0.5 }} />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative z-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <p className="text-sm font-extrabold uppercase tracking-widest text-pb-coral">Recipe Adventures</p>
            <h2 className="font-display font-black text-4xl md:text-5xl mt-3 leading-tight">
              One meal. <span className="wavy">Whole family.</span> Zero arguments.
            </h2>
            <p className="mt-4 text-pb-soft text-lg">
              Buddy-led recipes designed for real kitchens — small enough hands to help, big enough flavor for grown-ups. Every recipe doubles as a story your kid is starring in.
            </p>
          </div>
          <div className="flex gap-3">
            <button
              data-testid="btn-recipes-prev"
              onClick={() => scroll(-1)}
              className="h-12 w-12 rounded-full bg-white shadow border border-pb-sky/30 flex items-center justify-center hover:bg-pb-sky/40 transition"
              aria-label="Previous"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <button
              data-testid="btn-recipes-next"
              onClick={() => scroll(1)}
              className="h-12 w-12 rounded-full bg-pb-ink text-white shadow hover:bg-pb-coral transition flex items-center justify-center"
              aria-label="Next"
            >
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div
          ref={scrollerRef}
          className="mt-10 flex gap-6 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-8 pt-4 -mx-2 px-2"
        >
          {recipes.map((r, idx) => {
            const tilt = CARD_TILTS[idx % CARD_TILTS.length];
            const ingredientCount = Array.isArray(r.ingredients) ? r.ingredients.length : 0;
            // Pull the first noun of each of the first 3 ingredients for the sneak-peek chips.
            const previewIngredients = (r.ingredients || []).slice(0, 3).map((line) => {
              // "1 lb chicken breast, cubed" → "chicken breast"
              // "2 cups cooked white rice" → "cooked white rice"
              const noQty = String(line).replace(
                /^[\d\/\.\s\-]+(?:cups?|tbsps?|tsps?|teaspoons?|tablespoons?|lbs?|ozs?|cloves?|pounds?|ounces?|grams?|kg|g|jars?|cans?|packets?|slices?|large|small|medium|mini|fresh)?\s*/i,
                "",
              );
              return noQty.split(",")[0].split("(")[0].trim();
            });
            const recipeNum = String(idx + 1).padStart(2, "0");

            return (
              <Link
                to={`/recipes/${r.id}`}
                key={r.id}
                data-testid={`card-recipe-${r.id}`}
                className={`snap-start shrink-0 w-[300px] sm:w-[360px] group ${tilt} hover:rotate-0 transition-transform duration-500`}
              >
                <article className="relative bg-[#fffbf1] rounded-[1.75rem] overflow-hidden shadow-[0_18px_44px_rgba(28,22,40,0.10)] hover:shadow-[0_28px_60px_rgba(28,22,40,0.18)] transition-shadow duration-500 border border-pb-ink/[0.06]">
                  {/* image with corner ribbon */}
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={r.image}
                      alt={r.title}
                      className="w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-700"
                    />
                    {/* recipe-number "hand-stamped" badge */}
                    <div className="absolute top-4 left-4 flex items-center gap-2">
                      <span className="bg-pb-ink text-white px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-[0.14em]">
                        Recipe {recipeNum}
                      </span>
                      <span className="bg-white/95 backdrop-blur px-2.5 py-1 rounded-full text-[11px] font-extrabold text-pb-ink inline-flex items-center gap-1 shadow-sm">
                        <Clock className="h-3 w-3 text-pb-coral" /> {r.time}
                      </span>
                    </div>
                    {/* save-recipe icon button, top-right of image */}
                    <div className="absolute top-4 right-4">
                      <SaveRecipeButton recipe={r} variant="icon" />
                    </div>
                    {/* vibe pinned to bottom-right of image, like a magazine caption */}
                    {r.vibe && (
                      <span className="absolute bottom-3 right-3 bg-pb-coral text-white px-3 py-1 rounded-full text-[11px] font-extrabold shadow-md">
                        {r.vibe}
                      </span>
                    )}
                  </div>

                  {/* body */}
                  <div className="p-6 pt-5">
                    <h3 className="font-display font-black text-[1.35rem] leading-[1.1] text-pb-ink">
                      {r.title}
                    </h3>

                    {/* editorial pull-quote */}
                    {r.story && (
                      <p className="mt-2 text-[0.85rem] italic text-pb-soft line-clamp-2 leading-snug">
                        &ldquo;{r.story}&rdquo;
                      </p>
                    )}

                    {/* chip row: age · serves · ingredient count */}
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {r.kid_age && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-pb-sky/40 text-pb-ink text-[10px] font-bold uppercase tracking-wide">
                          <Baby className="h-3 w-3" /> {r.kid_age}
                        </span>
                      )}
                      {r.serves && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-pb-cream text-pb-ink text-[10px] font-bold uppercase tracking-wide border border-pb-ink/10">
                          <Utensils className="h-3 w-3" /> {r.serves}
                        </span>
                      )}
                      {ingredientCount > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-pb-coral/15 text-pb-coral text-[10px] font-bold uppercase tracking-wide">
                          <Sparkles className="h-3 w-3" /> {ingredientCount} ingredients
                        </span>
                      )}
                    </div>

                    {/* ingredient sneak-peek */}
                    {previewIngredients.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-dashed border-pb-ink/10">
                        <p className="text-[10px] font-black uppercase tracking-[0.14em] text-pb-soft">You&rsquo;ll grab</p>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {previewIngredients.map((ing, i) => (
                            <span
                              key={i}
                              className="px-2 py-0.5 rounded-md bg-white text-pb-ink text-[11px] font-semibold border border-pb-ink/10 capitalize"
                            >
                              {ing}
                            </span>
                          ))}
                          {ingredientCount > 3 && (
                            <span className="px-2 py-0.5 text-pb-soft text-[11px] font-semibold">
                              +{ingredientCount - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* CTA pill */}
                    <div className="mt-5 flex items-center justify-between">
                      <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-pb-ink text-white text-xs font-black uppercase tracking-wide shadow-sm group-hover:bg-pb-coral group-hover:gap-2.5 transition-all duration-300">
                        Cook it tonight <ArrowRight className="h-3.5 w-3.5" />
                      </span>
                      {r.kid_task && (
                        <span className="text-[10px] font-bold uppercase tracking-widest text-pb-coral text-right max-w-[110px] leading-tight">
                          Kid job inside
                        </span>
                      )}
                    </div>
                  </div>
                </article>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
